/**
 * Canonical Offer Metrics Resolver (P2)
 *
 * SINGLE SOURCE OF TRUTH for contractor accountability metrics.
 * Pure functions over (offers, jobs, systemEvents). No mutations.
 *
 * Locked rules (P2 spec):
 *   • Denominator = accepted + declined + expired
 *   • Withdrawn (dispatcher-initiated) and superseded (peer-won) are
 *     EXCLUDED from the denominator
 *   • Sample size < 3 → return null (NOT 0) to prevent defamation
 *   • Time window cutoff is INCLUSIVE: sentAt >= since
 *   • Median response time is computed only from accepted + declined
 *     (no response time exists for expired/withdrawn/superseded)
 *   • postAcceptReassignmentRate counts DISTINCT jobs reassigned
 *     (not event count) divided by accepts_with_outcome (excludes
 *     accepted jobs still in-flight). Same sample-size rule applies.
 *   • Decline reason distribution returns null when there are zero
 *     declines (do not return an empty map).
 *
 * Financial visibility: this resolver never reads or returns
 * the customer revenue field, financials.*, margin, or any internal pricing field.
 * Only payoutAmountSnapshot may be referenced through the offer object,
 * and even that is not aggregated here.
 *
 * Consumers MUST call these functions instead of recomputing rates
 * inline. See mem://index.md core rules.
 */

import type { Job } from "@/data/types";
import type { SystemEvent } from "@/data/communicationEngine";
import type {
  MarketplaceOffer,
  OfferDeclineReason,
} from "./marketplaceOffer";

export const MIN_SAMPLE_SIZE = 3;

export interface OfferMetricsOptions {
  /** Inclusive cutoff: only offers with sentAt >= since are counted. */
  since?: Date;
}

export interface OfferMetrics {
  /** Total terminal offers in the denominator (accepted+declined+expired). */
  sampleSize: number;
  /** Number of withdrawn offers (tracked, NOT in denominator). */
  withdrawnCount: number;
  /** Number of superseded offers (tracked, NOT in denominator). */
  supersededCount: number;
  /** Pending offers in the window (informational). */
  pendingCount: number;

  /** accepted / sampleSize. null when sampleSize < MIN_SAMPLE_SIZE. */
  acceptanceRate: number | null;
  declineRate: number | null;
  expiryRate: number | null;

  /** Withdrawn over (sampleSize + withdrawn). Informational only. null when insufficient. */
  withdrawnRate: number | null;

  /** ms; computed only from accepted + declined. null when fewer than MIN. */
  medianResponseTimeMs: number | null;

  /**
   * distinct reassigned-after-accept jobs / accepts_with_outcome.
   * "with outcome" = accepted offer whose job is no longer in-flight
   * (job is Completed OR contractor was unassigned away).
   * null when accepts_with_outcome < MIN_SAMPLE_SIZE.
   */
  postAcceptReassignmentRate: number | null;

  /** Counts breakdown for transparency. */
  counts: {
    accepted: number;
    declined: number;
    expired: number;
  };
}

/* ── helpers ─────────────────────────────────────────────────────── */

function inWindow(offer: MarketplaceOffer, since?: Date): boolean {
  if (!since) return true;
  return new Date(offer.sentAt).getTime() >= since.getTime();
}

function rate(num: number, den: number): number | null {
  if (den < MIN_SAMPLE_SIZE) return null;
  return num / den;
}

function median(values: number[]): number | null {
  if (values.length < MIN_SAMPLE_SIZE) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

function computeMetricsForOfferSet(
  offers: MarketplaceOffer[],
  jobs: Job[],
  systemEvents: SystemEvent[],
  options: OfferMetricsOptions = {},
): OfferMetrics {
  const windowed = offers.filter((o) => inWindow(o, options.since));

  const accepted = windowed.filter((o) => o.status === "accepted");
  const declined = windowed.filter((o) => o.status === "declined");
  const expired = windowed.filter((o) => o.status === "expired");
  const withdrawn = windowed.filter((o) => o.status === "withdrawn");
  const superseded = windowed.filter((o) => o.status === "superseded");
  const pending = windowed.filter((o) => o.status === "pending");

  const sampleSize = accepted.length + declined.length + expired.length;

  // Median response time — accepted + declined only.
  const responseTimes: number[] = [];
  for (const o of [...accepted, ...declined]) {
    if (!o.respondedAt) continue;
    const ms = new Date(o.respondedAt).getTime() - new Date(o.sentAt).getTime();
    if (ms >= 0) responseTimes.push(ms);
  }

  // Post-accept reassignment.
  // accepts_with_outcome: accepted offer where the job is "settled":
  //   - job currentStage === "Completed", OR
  //   - a contractor_unassigned event exists referencing this job +
  //     previousContractorId === this contractor (i.e., they were taken off).
  // distinct reassigned jobs: count distinct jobIds where a
  // contractor_unassigned event has previousContractorId === offer's contractorId.
  let acceptsWithOutcome = 0;
  const reassignedJobIds = new Set<string>();
  for (const acc of accepted) {
    const job = jobs.find((j) => j.id === acc.jobId);
    const wasUnassigned = systemEvents.some(
      (e) =>
        e.type === "contractor_unassigned" &&
        e.jobId === acc.jobId &&
        e.payload?.previousContractorId === acc.contractorId,
    );
    const settled = job?.currentStage === "Completed" || wasUnassigned;
    if (settled) acceptsWithOutcome++;
    if (wasUnassigned) reassignedJobIds.add(acc.jobId);
  }

  return {
    sampleSize,
    withdrawnCount: withdrawn.length,
    supersededCount: superseded.length,
    pendingCount: pending.length,
    acceptanceRate: rate(accepted.length, sampleSize),
    declineRate: rate(declined.length, sampleSize),
    expiryRate: rate(expired.length, sampleSize),
    withdrawnRate: rate(withdrawn.length, sampleSize + withdrawn.length),
    medianResponseTimeMs: median(responseTimes),
    postAcceptReassignmentRate: rate(reassignedJobIds.size, acceptsWithOutcome),
    counts: {
      accepted: accepted.length,
      declined: declined.length,
      expired: expired.length,
    },
  };
}

/* ── public API ─────────────────────────────────────────────────── */

export function getContractorOfferMetrics(
  contractorId: string,
  offers: MarketplaceOffer[],
  jobs: Job[],
  systemEvents: SystemEvent[] = [],
  options: OfferMetricsOptions = {},
): OfferMetrics {
  const subset = offers.filter((o) => o.contractorId === contractorId);
  return computeMetricsForOfferSet(subset, jobs, systemEvents, options);
}

export function getJobOfferMetrics(
  jobId: string,
  offers: MarketplaceOffer[],
  jobs: Job[],
  systemEvents: SystemEvent[] = [],
  options: OfferMetricsOptions = {},
): OfferMetrics {
  const subset = offers.filter((o) => o.jobId === jobId);
  return computeMetricsForOfferSet(subset, jobs, systemEvents, options);
}

/* ── decline reason distribution ────────────────────────────────── */

export type DeclineReasonDistribution = Partial<
  Record<OfferDeclineReason, number>
>;

/**
 * Returns the % distribution of decline reasons for a contractor.
 * Returns null when there are zero declines (do NOT return an empty map).
 * Values are percentages 0..100 and sum to 100 (with rounding tolerance ≤1).
 */
export function getDeclineReasonDistribution(
  contractorId: string,
  offers: MarketplaceOffer[],
  options: OfferMetricsOptions = {},
): DeclineReasonDistribution | null {
  const declines = offers.filter(
    (o) =>
      o.contractorId === contractorId &&
      o.status === "declined" &&
      inWindow(o, options.since),
  );
  if (declines.length === 0) return null;

  const counts: Record<string, number> = {};
  for (const d of declines) {
    const key = d.responseReason ?? "other";
    counts[key] = (counts[key] ?? 0) + 1;
  }
  const total = declines.length;
  const out: DeclineReasonDistribution = {};
  for (const [k, v] of Object.entries(counts)) {
    out[k as OfferDeclineReason] = (v / total) * 100;
  }
  return out;
}

/* ── history selectors (read-only) ──────────────────────────────── */

export function getContractorOfferHistory(
  contractorId: string,
  offers: MarketplaceOffer[],
  options: OfferMetricsOptions = {},
): MarketplaceOffer[] {
  return offers
    .filter((o) => o.contractorId === contractorId && inWindow(o, options.since))
    .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
}

export function getJobOfferHistory(
  jobId: string,
  offers: MarketplaceOffer[],
  options: OfferMetricsOptions = {},
): MarketplaceOffer[] {
  return offers
    .filter((o) => o.jobId === jobId && inWindow(o, options.since))
    .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
}

/* ── P4: Tier label resolver ────────────────────────────────────── */

import type { ReliabilityTier } from "@/data/contractorReliability";

const TIER_LABELS: Record<ReliabilityTier, string> = {
  Elite: "Elite",
  Reliable: "Reliable",
  Watch: "Watch",
  Risk: "Risk",
};

const TIER_DESCRIPTIONS: Record<ReliabilityTier, string> = {
  Elite:
    "You see the broadest range of opportunities and receive offers first.",
  Reliable:
    "You see most opportunities and receive offers regularly.",
  Watch:
    "You'll receive offers directly. Build a stronger track record to unlock browsing.",
  Risk:
    "Browsing is paused. Complete assigned work to regain access to opportunities.",
};

/** Canonical tier label. Forbid inline ternaries in UI. */
export function getReliabilityTierLabel(
  tier: ReliabilityTier | null | undefined,
): string {
  if (!tier) return "Unrated";
  return TIER_LABELS[tier];
}

/** Canonical plain-language tier description (contractor-facing). */
export function getReliabilityTierDescription(
  tier: ReliabilityTier | null | undefined,
): string {
  if (!tier)
    return "We're still learning your performance — complete a few offers to unlock more visibility.";
  return TIER_DESCRIPTIONS[tier];
}

/* ── P4: Dispatcher labels (light context, no raw metrics) ──────── */

export type DispatcherLabelTone = "positive" | "neutral" | "warning";

export interface DispatcherLabel {
  key: "high_reliability" | "new_contractor" | "frequent_expirations";
  label: string;
  tone: DispatcherLabelTone;
}

const DISPATCHER_LABEL_TEXT: Record<DispatcherLabel["key"], string> = {
  high_reliability: "High reliability contractor",
  new_contractor: "New contractor (low data)",
  frequent_expirations: "Frequent expirations",
};

/** Threshold: expiry rate at/above this is "frequent". */
export const FREQUENT_EXPIRATION_THRESHOLD = 0.3;

/**
 * Resolve dispatcher-facing context labels for a contractor.
 *
 * Rules (P4 locked):
 *   • Sample size < MIN_SAMPLE_SIZE → only "new_contractor" label.
 *   • Behavioral labels (high_reliability, frequent_expirations) require
 *     sufficient sample.
 *   • No raw metrics exposed; this returns labels only.
 *   • Reliability tier comes from the caller's existing scorecard path
 *     (we don't recompute) — pass it in to keep this pure.
 */
export function getContractorDispatcherLabels(
  contractorId: string,
  offers: MarketplaceOffer[],
  jobs: Job[],
  systemEvents: SystemEvent[],
  tier: ReliabilityTier | null,
  options: OfferMetricsOptions = {},
): DispatcherLabel[] {
  const metrics = getContractorOfferMetrics(
    contractorId,
    offers,
    jobs,
    systemEvents,
    options,
  );
  const out: DispatcherLabel[] = [];
  const insufficient = metrics.sampleSize < MIN_SAMPLE_SIZE;

  if (insufficient) {
    out.push({
      key: "new_contractor",
      label: DISPATCHER_LABEL_TEXT.new_contractor,
      tone: "neutral",
    });
    return out;
  }

  if (tier === "Elite" || tier === "Reliable") {
    out.push({
      key: "high_reliability",
      label: DISPATCHER_LABEL_TEXT.high_reliability,
      tone: "positive",
    });
  }
  if (
    metrics.expiryRate != null &&
    metrics.expiryRate >= FREQUENT_EXPIRATION_THRESHOLD
  ) {
    out.push({
      key: "frequent_expirations",
      label: DISPATCHER_LABEL_TEXT.frequent_expirations,
      tone: "warning",
    });
  }
  return out;
}
